import { useState } from 'react';
import type { GetSalesOrderResponse, SalesOrderStatus } from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCartIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  NoteIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Currency } from '@/components/common/Currency';
import { T, Var, msg, useMessages, useGT } from 'gt-react';

const SALES_ORDER_STATUS_LABELS: Record<SalesOrderStatus, string> = {
  pending: msg('Pending', {
    $context: 'sales order workflow status label',
  }) as string,
  in_progress: msg('In Progress', {
    $context: 'sales order workflow status label',
  }) as string,
  fulfilled: msg('Fulfilled', {
    $context: 'sales order workflow status label',
  }) as string,
  closed: msg('Closed', {
    $context: 'sales order workflow status label',
  }) as string,
};

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'pending':
      return 'secondary';
    case 'in_progress':
      return 'default';
    case 'fulfilled':
      return 'outline';
    case 'closed':
      return 'outline';
    default:
      return 'secondary';
  }
}

/** Title-case a snake_case status from integrations (e.g. shipment). */
function formatSnakeCaseStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type SalesOrderWithDetails = NonNullable<GetSalesOrderResponse['salesOrder']>;

interface SalesOrderMetadataProps {
  salesOrder: SalesOrderWithDetails;
}

export function SalesOrderMetadata({ salesOrder }: SalesOrderMetadataProps) {
  const gt = useGT();
  const m = useMessages();

  const notAvailableLabel = gt('N/A', {
    $context: 'placeholder when a currency or numeric value is not available',
  });

  const unknownCustomerLabel = gt('Unknown Customer', {
    $context: 'shown when sales order has no customer name',
  });

  const statusLabelEncoded = SALES_ORDER_STATUS_LABELS[salesOrder.status];
  const statusLabel = statusLabelEncoded
    ? m(statusLabelEncoded)
    : salesOrder.status;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCartIcon className="h-4 w-4" />
            {salesOrder.originId ?? (
              <T context="sales order number from internal ID when origin ID missing">
                SO-<Var>{salesOrder.internalId}</Var>
              </T>
            )}
          </CardTitle>
          <Badge variant={getStatusVariant(salesOrder.status)}>
            {statusLabel}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Main Sales Order Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              <T context="label for customer on sales order detail">Customer</T>
            </div>
            <div className="text-sm font-medium">
              {salesOrder.customer?.name || unknownCustomerLabel}
            </div>
            {salesOrder.customer?.customerNumber && (
              <div className="text-xs text-muted-foreground">
                <T context="customer account number prefix on sales order">
                  #<Var>{salesOrder.customer.customerNumber}</Var>
                </T>
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T context="label for sales order total amount">Total Amount</T>
            </div>
            <div className="text-lg font-bold text-green-600">
              <Currency
                value={salesOrder.totalAmount}
                currency={salesOrder.currencyCode || 'USD'}
                fallback={notAvailableLabel}
              />
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T context="label for sales order date">Order Date</T>
            </div>
            <div className="font-medium text-sm">
              {formatDateOnlyString(salesOrder.orderDate)}
            </div>
          </div>

          {salesOrder.deliveryDate && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <T context="label for promised delivery date">Delivery Date</T>
              </div>
              <div className="font-medium text-sm">
                {formatDateOnlyString(salesOrder.deliveryDate)}
              </div>
            </div>
          )}

          {salesOrder.shipDate && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                <T context="label for shipment date">Ship Date</T>
              </div>
              <div className="font-medium text-sm">
                {formatDateOnlyString(salesOrder.shipDate)}
              </div>
            </div>
          )}

          {salesOrder.customerPurchaseOrderNumber && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ShoppingCartIcon className="h-3 w-3" />
                <T context="label for customer's purchase order number">
                  Customer PO #
                </T>
              </div>
              <div className="font-medium text-sm">
                {salesOrder.customerPurchaseOrderNumber}
              </div>
            </div>
          )}
        </div>

        {/* Shipment Info */}
        {salesOrder.shipment && (
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <TruckIcon className="h-3 w-3" />
              <T context="label for shipment linked to sales order">
                Linked Shipment
              </T>
            </div>
            <div className="text-sm">
              <span className="font-medium">
                {salesOrder.shipment.originLoadNumber ||
                  salesOrder.shipment.id.slice(0, 8)}
              </span>
              {salesOrder.shipment.origin &&
                salesOrder.shipment.destination && (
                  <span className="text-muted-foreground ml-2">
                    {salesOrder.shipment.origin}{' '}
                    <T context="arrow between shipment origin and destination">
                      →
                    </T>{' '}
                    {salesOrder.shipment.destination}
                  </span>
                )}
              {salesOrder.shipment.status && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {formatSnakeCaseStatus(salesOrder.shipment.status)}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Source Info */}
        <div className="border-t pt-3">
          <div className="text-xs text-muted-foreground">
            <T context="sales order data source label">
              Source: <Var>{salesOrder.source}</Var>
            </T>
            {salesOrder.originSystem && (
              <span>
                {' '}
                <T context="sales order origin system name">
                  | Origin: <Var>{salesOrder.originSystem}</Var>
                </T>
              </span>
            )}
          </div>
        </div>

        {/* Notes */}
        {salesOrder.notes && salesOrder.notes.length > 0 && (
          <NotesSection notes={salesOrder.notes} />
        )}
      </CardContent>
    </Card>
  );
}

function NotesSection({
  notes,
}: {
  notes: NonNullable<SalesOrderWithDetails['notes']>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t pt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground w-full"
      >
        <NoteIcon className="h-3 w-3" />
        <T context="expandable notes section with count">
          Notes (<Var>{notes.length}</Var>)
        </T>
        <CaretDownIcon
          className={`h-3 w-3 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <ul className="mt-2 space-y-1.5">
          {notes.map(function renderNote(note, index) {
            return (
              <li
                key={index}
                className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5"
              >
                {note.text}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
